import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

type ReportStatus = 'OPEN' | 'REVIEWING' | 'ACTIONED' | 'DISMISSED';

interface InboxReport {
  id: string;
  eventId: string;
  source: string;
  reporterId: string;
  targetType: string;
  targetId: string;
  reason: string;
  contextJson: string | null;
  status: ReportStatus;
  resolutionNote: string | null;
  resolvedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_LABEL: Record<ReportStatus, string> = {
  OPEN: '새 신고',
  REVIEWING: '검토 중',
  ACTIONED: '조치함',
  DISMISSED: '기각',
};
const TARGET_LABEL: Record<string, string> = { user: '사용자', lounge_message: '라운지 글' };
const PAGE_SIZE = 30;

/**
 * 서비스 신고 처리(specs/038 INBOX-REQ-02). inbox-server 운영자 API 를 /api/inbox rewrite 로 부른다
 * (vercel.json·vite.config.ts). 권한은 inbox-server 가 SCOPE_admin·ROLE_ADMIN 으로 확인한다.
 */
export default function UserReportsPage() {
  const { token: accessToken } = useAuth();
  const [status, setStatus] = useState<ReportStatus | ''>('OPEN');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<InboxReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const headers = useCallback(
    () => (accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    [accessToken],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get<{ items: InboxReport[]; total: number }>('/api/inbox/admin/reports', {
        headers: headers(),
        params: { ...(status ? { status } : {}), limit: PAGE_SIZE, offset },
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(axios.isAxiosError(e) ? `신고를 불러오지 못했어요 (${e.response?.status ?? '네트워크'})` : String(e));
    } finally {
      setLoading(false);
    }
  }, [headers, status, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = async (report: InboxReport, next: ReportStatus) => {
    setError(null);
    try {
      await axios.post(
        `/api/inbox/admin/reports/${encodeURIComponent(report.id)}/status`,
        { status: next, note: notes[report.id]?.trim() || null },
        { headers: headers() },
      );
      await load();
    } catch (e) {
      setError(axios.isAxiosError(e) ? `처리하지 못했어요 (${e.response?.status ?? '네트워크'})` : String(e));
    }
  };

  const context = (json: string | null): string => {
    if (!json) return '';
    try {
      const parsed = JSON.parse(json) as Record<string, unknown>;
      return Object.entries(parsed).map(([k, v]) => `${k}: ${String(v)}`).join(' · ');
    } catch {
      return json;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>신고 처리</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2" data-testid="report-status-filter">
          {(['OPEN', 'REVIEWING', 'ACTIONED', 'DISMISSED', ''] as const).map((s) => (
            <Button
              key={s || 'all'}
              variant={status === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setStatus(s);
                setOffset(0);
              }}
            >
              {s ? STATUS_LABEL[s] : '전체'}
            </Button>
          ))}
          <span className="text-sm text-muted-foreground" data-testid="report-total">
            {loading ? '불러오는 중…' : `${total}건`}
          </span>
        </div>
        {error && <p className="text-sm text-red-600" data-testid="report-error">{error}</p>}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>접수</TableHead>
              <TableHead>서비스·대상</TableHead>
              <TableHead>사유·내용</TableHead>
              <TableHead>신고자</TableHead>
              <TableHead>상태</TableHead>
              <TableHead>처리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">신고가 없어요</TableCell>
              </TableRow>
            ) : (
              items.map((r) => (
                <TableRow key={r.id} data-testid="report-row">
                  <TableCell className="whitespace-nowrap">{new Date(r.createdAt).toLocaleString('ko-KR')}</TableCell>
                  <TableCell>
                    {r.source} · {TARGET_LABEL[r.targetType] ?? r.targetType}
                    <div className="text-xs text-muted-foreground">{r.targetId}</div>
                  </TableCell>
                  <TableCell className="max-w-md">
                    {r.reason}
                    {r.contextJson && <div className="text-xs text-muted-foreground break-all">{context(r.contextJson)}</div>}
                  </TableCell>
                  <TableCell className="text-xs">{r.reporterId}</TableCell>
                  <TableCell>
                    <Badge variant={r.status === 'OPEN' ? 'destructive' : 'secondary'}>{STATUS_LABEL[r.status]}</Badge>
                    {r.resolutionNote && <div className="text-xs text-muted-foreground">{r.resolutionNote}</div>}
                    {r.resolvedBy && <div className="text-xs text-muted-foreground">by {r.resolvedBy}</div>}
                  </TableCell>
                  <TableCell className="space-y-1">
                    <Input
                      placeholder="메모(선택)"
                      value={notes[r.id] ?? ''}
                      onChange={(e) => setNotes((prev) => ({ ...prev, [r.id]: e.target.value.slice(0, 1000) }))}
                    />
                    <div className="flex flex-wrap gap-1">
                      {(['REVIEWING', 'ACTIONED', 'DISMISSED'] as const)
                        .filter((s) => s !== r.status)
                        .map((s) => (
                          <Button key={s} size="sm" variant="outline" onClick={() => void change(r, s)}>
                            {STATUS_LABEL[s]}
                          </Button>
                        ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            이전
          </Button>
          <span className="text-sm">{total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)} / {total}</span>
          <Button size="sm" variant="outline" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
            다음
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
